import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { CustomToast } from "@components/common/Alert";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

// Codes mirror scribenote-backend's BE-2308 email hardening:
// permission_checks.py (EMAIL_NOT_CONFIRMED), modules/rate_limit/
// exceptions.py (RATE_LIMITED), utils/email_helpers.py (INVALID_MESSAGE).
export const EMAIL_NOT_CONFIRMED_CODE = "EMAIL_NOT_CONFIRMED";
export const RATE_LIMITED_CODE = "RATE_LIMITED";
export const INVALID_MESSAGE_CODE = "INVALID_MESSAGE";

export const HANDLED_EMAIL_FLOW_CODES = [
  EMAIL_NOT_CONFIRMED_CODE,
  RATE_LIMITED_CODE,
  INVALID_MESSAGE_CODE,
];

/**
 * True when the global Apollo errorLink already surfaced this error's toast
 * (unconfirmed email, rate limited, or rejected message content), so
 * call-site catch blocks should skip their own generic error toast.
 */
export function isHandledEmailFlowError(error) {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some((err) =>
      HANDLED_EMAIL_FLOW_CODES.includes(err.extensions?.code),
    )
  );
}

function EmailNotConfirmedToast({ closeToast }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-row items-start text-white w-[300px] p-4">
      <div className="flex text-xl mr-3 items-center">
        <FontAwesomeIcon className="inline" icon={faBell} />
      </div>
      <div className="flex flex-col items-start space-y-2">
        <span>
          Please confirm your email address to send emails from
          Scribenote.
        </span>
        <button
          type="button"
          className="font-semibold underline"
          data-testid="email-not-confirmed-cta"
          onClick={() => {
            closeToast?.();
            navigate("/registration_confirmation");
          }}
        >
          Confirm my email
        </button>
      </div>
    </div>
  );
}

let activeConfirmEmailToastId = null;

// Confirm-email toast with a CTA to /registration_confirmation (which owns
// resend + code entry). Dismisses the previous toast and creates a fresh one
// so every blocked attempt re-surfaces — call sites suppress their own
// alerts, so a swallowed toast means total silence. A fresh id each call is
// load-bearing: react-toastify drops a create whose toastId is still in the
// container map, and with autoClose: false a fixed id stays mapped until its
// exit animation ends — a fixed-id dismiss-then-reshow loses the toast
// entirely.
function showEmailNotConfirmedToast() {
  if (activeConfirmEmailToastId != null) {
    toast.dismiss(activeConfirmEmailToastId);
  }
  activeConfirmEmailToastId = toast.error(
    <EmailNotConfirmedToast />,
    {
      autoClose: false,
    },
  );
}

function rateLimitedMessage(message, retryAfterSeconds) {
  // retryAfterSeconds = 0 means retrying as-is can never succeed (e.g. a
  // batch larger than the whole budget) — show the server's message, it
  // says why. With a positive window the frontend owns the whole sentence
  // instead of splicing the server copy (see PR #3175 review).
  if (!retryAfterSeconds || retryAfterSeconds <= 0) {
    return message;
  }
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  return `You're doing that too often. Try again in ~${minutes} minute${minutes === 1 ? "" : "s"}.`;
}

/**
 * Global errorLink hook for the BE-2308 email-flow error codes. Shows the
 * confirm-email CTA toast for EMAIL_NOT_CONFIRMED, and the server's own
 * message (plus a retry hint from extensions.retryAfterSeconds) for
 * RATE_LIMITED / INVALID_MESSAGE. Deduped per code via toastId. Returns
 * true when a toast was shown.
 */
export function showEmailFlowErrorToast(error) {
  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }

  let handled = false;
  for (const err of error.errors) {
    const code = err.extensions?.code;
    if (code === EMAIL_NOT_CONFIRMED_CODE) {
      showEmailNotConfirmedToast();
      handled = true;
    } else if (
      code === RATE_LIMITED_CODE ||
      code === INVALID_MESSAGE_CODE
    ) {
      const message =
        code === RATE_LIMITED_CODE
          ? rateLimitedMessage(
              err.message,
              err.extensions?.retryAfterSeconds,
            )
          : err.message;
      const toastId = `email-flow-${code}`;
      const content = <CustomToast message={message} />;
      // react-toastify drops a new toast whose toastId is already active;
      // these messages are dynamic (retry window, rejection reason), so a
      // repeat failure must refresh the visible toast, not be swallowed.
      if (toast.isActive(toastId)) {
        toast.update(toastId, { render: content });
      } else {
        toast.error(content, { toastId, autoClose: false });
      }
      handled = true;
    }
  }
  return handled;
}
